import { round } from "lodash";
import numbro from "numbro";
import { Customer } from "server/dwhApi/customers/types.js";

import { GraphData, Translation } from "./chart";
import { ComparisonOptions } from "./index";

export type GroupedCustomers = {
  sortingDegree: number;
  customers: Customer[];
};

export const getOrganizedCustomerData = ({
  tenantsData,
  comparisonOption,
  commercialCustomerCategory,
}: {
  tenantsData: Customer[];
  comparisonOption: string;
  commercialCustomerCategory?: string;
}): GroupedCustomers[] => {
  let filteredData = tenantsData;
  if (
    comparisonOption === ComparisonOptions.SIMILAR_TENANTS &&
    !!commercialCustomerCategory
  ) {
    filteredData = filteredData.filter(
      (d) => d.commercialCustomerCategory === commercialCustomerCategory,
    );
  }

  // group tenants by sorting degree as position
  const sortedGroupedCustomers = filteredData
    .reduce((acc, customer) => {
      const existingSortingDegreeIndex = acc.findIndex(
        (el) => el.sortingDegree === customer.sortingDegree,
      );

      if (existingSortingDegreeIndex >= 0) {
        acc[existingSortingDegreeIndex].customers.push(customer);
        return acc;
      } else {
        acc.push({
          sortingDegree: customer.sortingDegree,
          customers: [customer],
        });
      }

      return acc;
    }, [] as GroupedCustomers[])
    .sort((a, b) => b.sortingDegree - a.sortingDegree);

  return sortedGroupedCustomers;
};

export const getFormattedOrdinal = (index: number): string =>
  numbro(index).format({
    output: "ordinal",
  });

export const getStandardRankingOrdinalPosition = (
  data: GroupedCustomers[],
  index: number,
): string => {
  const previousPositions = data.slice(0, index);
  const currentPosition =
    previousPositions.reduce((acc, group) => {
      return acc + group.customers.length;
    }, 0) + 1;

  return getFormattedOrdinal(currentPosition);
};

export const getBestPositionData = ({
  sortedGroupedCustomers,
  currentTenant,
}: {
  sortedGroupedCustomers: GroupedCustomers[];
  currentTenant: Customer;
}): GraphData => {
  const bestPosition = sortedGroupedCustomers[0];
  const bestOrdinal = getFormattedOrdinal(1);

  const bestTitle: Translation = {
    key: "",
    ordinalString: bestOrdinal,
  };

  let barColor = "#E7EB5D";

  if (bestPosition.sortingDegree !== currentTenant.sortingDegree) {
    // add best position that doesn't include current tenant
    bestTitle.key = "circularityLabels:comparisonPositionWithName";

    bestTitle.customerName = bestPosition.customers[0].customerName;
    barColor = "#DAD3D3";
  } else if (bestPosition.customers.length === 1) {
    // add best position only current tenant
    bestTitle.key = "circularityLabels:comparisonPositionYou";
  } else {
    // add shared best position
    const otherBest = bestPosition.customers.filter(
      (c) => c.customerId !== currentTenant.customerId,
    );

    bestTitle.key =
      otherBest.length > 1
        ? "circularityLabels:comparisonPositionSharedMultiple"
        : "circularityLabels:comparisonPositionSharedTwo";

    bestTitle.customerName = otherBest[0].customerName;
    bestTitle.numberOfTies = otherBest.length;
  }

  return {
    title: bestTitle,
    value: round(bestPosition.sortingDegree * 100, 1),
    color: barColor,
  };
};

export const getPositionInFrontData = ({
  sortedGroupedCustomers,
  currentTenant,
}: {
  sortedGroupedCustomers: GroupedCustomers[];
  currentTenant: Customer;
}): GraphData | null => {
  const currentTenantIndex = sortedGroupedCustomers.findIndex(
    (g) => g.sortingDegree === currentTenant.sortingDegree,
  );
  // add tenant in front if exists and is not best tenant
  if (currentTenantIndex > 1) {
    const customerInFront = sortedGroupedCustomers[currentTenantIndex - 1];
    const ordinalStringPosition = getStandardRankingOrdinalPosition(
      sortedGroupedCustomers,
      currentTenantIndex - 1,
    );
    return {
      title: {
        key: "circularityLabels:comparisonPositionWithName",
        ordinalString: ordinalStringPosition,
        customerName: customerInFront.customers[0].customerName,
      },
      value: round(customerInFront.sortingDegree * 100, 1),
      color: "#DAD3D3",
    };
  }

  return null;
};

export const getCurrentPositionData = ({
  sortedGroupedCustomers,
  currentTenant,
}: {
  sortedGroupedCustomers: GroupedCustomers[];
  currentTenant: Customer;
}): GraphData | null => {
  const currentTenantIndex = sortedGroupedCustomers.findIndex(
    (g) => g.sortingDegree === currentTenant.sortingDegree,
  );

  if (currentTenantIndex > 0) {
    const othersOnSamePosition = sortedGroupedCustomers[
      currentTenantIndex
    ].customers.filter((c) => c.customerId !== currentTenant.customerId);

    const ordinalStringPosition = getStandardRankingOrdinalPosition(
      sortedGroupedCustomers,
      currentTenantIndex,
    );

    const currentPosition: GraphData = {
      title: {
        key: "circularityLabels:comparisonPositionYou",
        ordinalString: ordinalStringPosition,
      },
      value: round(currentTenant.sortingDegree * 100, 1),
      color: "#E7EB5D",
    };

    if (othersOnSamePosition.length > 0) {
      // add shared current tenant position
      currentPosition.title = {
        key:
          othersOnSamePosition.length > 1
            ? "circularityLabels:comparisonPositionSharedMultiple"
            : "circularityLabels:comparisonPositionSharedTwo",
        ordinalString: ordinalStringPosition,
        customerName: othersOnSamePosition[0].customerName,
        numberOfTies: othersOnSamePosition.length,
      };
    }

    return currentPosition;
  }

  return null;
};

export const getPositionBehindData = ({
  sortedGroupedCustomers,
  currentTenant,
}: {
  sortedGroupedCustomers: GroupedCustomers[];
  currentTenant: Customer;
}): GraphData | null => {
  const currentTenantIndex = sortedGroupedCustomers.findIndex(
    (g) => g.sortingDegree === currentTenant.sortingDegree,
  );

  if (currentTenantIndex < sortedGroupedCustomers.length - 1) {
    const customerBehind = sortedGroupedCustomers[currentTenantIndex + 1];
    const ordinalStringPosition = getStandardRankingOrdinalPosition(
      sortedGroupedCustomers,
      currentTenantIndex + 1,
    );
    return {
      title: {
        key: "circularityLabels:comparisonPositionWithName",
        ordinalString: ordinalStringPosition,
        customerName: customerBehind.customers[0].customerName,
      },
      value: round(customerBehind.sortingDegree * 100, 1),
      color: "#DAD3D3",
    };
  }

  return null;
};
